import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { usePermanentRealtimeDealers } from "components";
import { createFormHelper } from "lib/Form";
import "modules/DMSv3/Settings/components/AddEditLocationModal/AddEditLocationModal.scss";
import { AddEditLocationForm } from "modules/DMSv3/Settings/components/AddEditLocationModal/components";
import { useCreateLocationMutation, useUpdateLocationMutation } from "modules/DMSv3/Settings/components/AddEditLocationModal/hooks";
import { useDMSv3DealersListQuery } from "modules/DMSv3/Settings/components/DMSv3SettingsTable/hooks";

import type { DMSIDS, DealerDMS, DealerLocation } from "models";
import type { AutolineConfig, ICarConfig, IncadeaConfig, KeyloopConfig } from "modules/DMSv3/Settings/components/AddEditLocationModal/components";

export type DmsLocationConfig = {
  dealer_location_dms_id: number | null;
  dealer_id: number | null;
  comment: string;
  dms_location_id: string;
  dms_database: string;
  maintenance_intervention: string;
  customer_not_approved: string;
  not_visible_to_customer: string;
  exclude_from_import: string;
  dms_id?: DMSIDS;
  keyloop_config?: KeyloopConfig;
  incadea_config?: IncadeaConfig;
  autoline_config?: AutolineConfig;
  icar_config?: ICarConfig;
};

export type AddEditLocationFormPayload = Pick<DealerLocation, "dms_write_back" | "dms_warranty_pin" | "is_dms_event_enabled" | "keep_importing_after_work_started"> & {
  dealer_location_id: number | null;
  dsn: string;
  automatic_appointment_refresh_interval: number | null;
  autoflex_username: string;
  autoflex_password: string;
  webhook_low_threshold_per_minute: number | null;
  dealer_location_dms: DmsLocationConfig[];
  dms_id?: DMSIDS;
};

type AddEditLocationModalProps = {
  isOpen: boolean;
  selectedLocationDealer: DealerDMS | undefined;
  selectedLocation: DealerLocation | undefined;
  onClose: () => void;
};

const FormHelper = createFormHelper<AddEditLocationFormPayload>();

export const AddEditLocationModal = ({ isOpen, selectedLocation, selectedLocationDealer, onClose }: AddEditLocationModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);
  const [dmsDateRangeLimit, setDmsDateRangeLimit] = useState<number | null>(null);

  const { dealers } = usePermanentRealtimeDealers();
  const { data: dmsDealersList = [] } = useDMSv3DealersListQuery();
  const { mutateAsync: createLocation, isPending: isCreateLocationPending } = useCreateLocationMutation();
  const { mutateAsync: updateLocation, isPending: isUpdateLocationPending } = useUpdateLocationMutation();

  const handleClose = () => {
    onClose();
    setHasErrors(false);
    setDmsDateRangeLimit(null);
  };

  const isFormInvalid = (values: AddEditLocationFormPayload) => {
    const location = dealers.flatMap(dealer => dealer.locations ?? []).find(location => location.id === values.dealer_location_id);
    const dms = location && dmsDealersList.find(dms => dms.dealer_id === location.dealer_id && dms.dms_id === values.dms_id);
    const isDateRangeTooLow = !!location?.online_check_in_enabled && !!dms && dms.date_range <= location.check_in_days_prior;

    const hasErrors = !values.dealer_location_id || isDateRangeTooLow;
    setDmsDateRangeLimit(isDateRangeTooLow ? dms.date_range : null);
    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: AddEditLocationFormPayload) => {
    if (isFormInvalid(values)) return;

    selectedLocation ? await updateLocation(values) : await createLocation(values);
    handleClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} closeOnDimmerClick={false} size="large">
      <div className="AddEditLocationModal">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={
            selectedLocation
              ? {
                  dealer_location_id: selectedLocation.id,
                  dsn: selectedLocation.dsn,
                  automatic_appointment_refresh_interval: selectedLocation.automatic_appointment_refresh_interval,
                  webhook_low_threshold_per_minute: selectedLocation.webhook_low_threshold_per_minute,
                  dms_warranty_pin: selectedLocation.dms_warranty_pin,
                  dms_write_back: selectedLocation.dms_write_back,
                  is_dms_event_enabled: selectedLocation.is_dms_event_enabled,
                  keep_importing_after_work_started: selectedLocation.keep_importing_after_work_started,
                  autoflex_password: "",
                  autoflex_username: selectedLocation.autoflex_username,
                  dms_id: selectedLocation.dms_id,
                  dealer_location_dms:
                    selectedLocation.dealer_location_dms?.map(dld => ({
                      dealer_location_dms_id: dld.dealer_location_dms_id,
                      dealer_id: null,
                      dms_id: undefined,
                      comment: dld.comment,
                      dms_location_id: dld.dms_location_id,
                      dms_database: dld.dms_database,
                      maintenance_intervention: dld.maintenance_intervention,
                      customer_not_approved: dld.customer_not_approved,
                      not_visible_to_customer: dld.not_visible_to_customer,
                      exclude_from_import: dld.exclude_from_import,
                      keyloop_config: dld.keyloop_config,
                      incadea_config: dld.incadea_config,
                      autoline_config: dld.autoline_config,
                      icar_config: dld.icar_config
                    })) || []
                }
              : {
                  dealer_location_id: null,
                  dsn: "",
                  automatic_appointment_refresh_interval: null,
                  webhook_low_threshold_per_minute: null,
                  dms_warranty_pin: false,
                  dms_write_back: false,
                  is_dms_event_enabled: false,
                  keep_importing_after_work_started: false,
                  autoflex_password: "",
                  autoflex_username: "",
                  dms_id: selectedLocationDealer?.dms_id,
                  dealer_location_dms: []
                }
          }
        >
          <AddEditLocationForm
            hasErrors={hasErrors}
            dmsDateRangeLimit={dmsDateRangeLimit}
            isPending={isCreateLocationPending || isUpdateLocationPending}
            selectedLocation={selectedLocation}
            selectedLocationDealer={selectedLocationDealer}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
